import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';

import { theme } from '../../../../theme';

const STATUS_LABELS = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  cancelled: 'Annulé',
  completed: 'Terminé',
  reschedule_required: 'À replanifier',
  proposal_pending: 'Proposition en attente',
  proposal_expired: 'Proposition expirée',
};

const STATUS_COLORS = {
  pending: 'warning',
  confirmed: 'success',
  cancelled: 'error',
  completed: 'default',
  reschedule_required: 'warning',
  proposal_pending: 'info',
  proposal_expired: 'default',
};

const formatStatus = (status) => {
  if (!status) return '';
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  const normalized = String(status).replaceAll('_', ' ');
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const AppointmentConversationSummary = ({ appointment }) => {
  if (!appointment) return null;

  const serviceName = appointment.service_name || '';
  const formattedDate = formatDateTime(appointment.start_time);
  const statusLabel = formatStatus(appointment.status);
  const appointmentNumber = appointment.id != null
    ? `RDV #${appointment.id}`
    : '';

  if (
    !serviceName
    && !formattedDate
    && !statusLabel
    && !appointmentNumber
  ) {
    return null;
  }

  return (
    <Box
      aria-label="Détails du rendez-vous"
      sx={{
        mx: 1.5,
        my: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: '16px',
        border: `1px solid ${theme.colors.primary[200]}`,
        background: `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.secondary[50]} 100%)`,
        boxShadow: '0 7px 18px rgba(107, 56, 26, 0.07)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '10px',
            color: theme.colors.primary[950],
            backgroundColor: theme.colors.primary[100],
          }}
        >
          <ContentCutRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {serviceName && (
            <Typography
              noWrap
              variant="body2"
              sx={{ fontWeight: 800, color: theme.colors.secondary[950] }}
            >
              {serviceName}
            </Typography>
          )}
          {appointmentNumber && (
            <Typography
              variant="caption"
              sx={{ color: theme.colors.grey[600], fontWeight: 700 }}
            >
              {appointmentNumber}
            </Typography>
          )}
        </Box>
        {statusLabel && (
          <Chip
            size="small"
            label={statusLabel}
            color={STATUS_COLORS[appointment.status] || 'default'}
            sx={{
              height: 24,
              flexShrink: 0,
              fontSize: '0.72rem',
              fontWeight: 800,
            }}
          />
        )}
      </Box>
      {formattedDate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 1,
            color: theme.colors.primary[900],
          }}
        >
          <CalendarMonthRoundedIcon sx={{ fontSize: 17 }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {formattedDate}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AppointmentConversationSummary;
